// Wishlist — DB-backed for signed-in users, localStorage for guests.
// On sign-in, guest wishlist merges into DB (same pattern as cart).

import { supabase } from './supabase.js';

const STORAGE_KEY = 'se_wishlist_v1';

// ── localStorage helpers (guest) ──
function readLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(x => Number.isInteger(x)) : [];
  } catch { return []; }
}

function writeLocal(ids) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)); } catch {}
}

function clearLocal() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

// ── Public API ──

/** Get all wishlisted product IDs for the current user. */
export async function getWishlistIds(userId) {
  if (!userId) return readLocal();
  try {
    const { data, error } = await supabase
      .from('wishlists').select('product_id').eq('user_id', userId);
    if (error) throw error;
    return (data || []).map(r => r.product_id);
  } catch {
    return readLocal(); // fallback
  }
}

/** Add a product to the wishlist. */
export async function addToWishlist(userId, productId) {
  const pid = Number(productId);
  if (!Number.isInteger(pid)) return;
  if (!userId) {
    const ids = readLocal();
    if (!ids.includes(pid)) { ids.unshift(pid); writeLocal(ids); }
    return;
  }
  try {
    await supabase.from('wishlists')
      .upsert({ user_id: userId, product_id: pid }, { onConflict: 'user_id,product_id' });
  } catch {}
  // Also keep localStorage in sync for instant UI
  const ids = readLocal();
  if (!ids.includes(pid)) { ids.unshift(pid); writeLocal(ids); }
}

/** Remove a product from the wishlist. */
export async function removeFromWishlist(userId, productId) {
  const pid = Number(productId);
  if (!Number.isInteger(pid)) return;
  if (!userId) {
    writeLocal(readLocal().filter(x => x !== pid));
    return;
  }
  try {
    await supabase.from('wishlists')
      .delete().eq('user_id', userId).eq('product_id', pid);
  } catch {}
  writeLocal(readLocal().filter(x => x !== pid));
}

/** Toggle wishlist state. Returns true if now wishlisted, false if removed. */
export async function toggleWishlist(userId, productId) {
  const pid = Number(productId);
  const ids = await getWishlistIds(userId);
  if (ids.includes(pid)) {
    await removeFromWishlist(userId, productId);
    return false;
  } else {
    await addToWishlist(userId, productId);
    return true;
  }
}

/** Merge guest localStorage wishlist into server on sign-in. */
export async function mergeWishlistOnSignIn(userId) {
  if (!userId) return;
  const guestIds = readLocal();
  if (guestIds.length === 0) return;
  try {
    const rows = guestIds.map(pid => ({ user_id: userId, product_id: pid }));
    await supabase.from('wishlists')
      .upsert(rows, { onConflict: 'user_id,product_id' });
  } catch {}
}

/** Sync localStorage from server (call after sign-in / merge). */
export async function syncWishlistFromServer(userId) {
  if (!userId) return;
  try {
    const ids = await getWishlistIds(userId);
    writeLocal(ids);
  } catch {}
}

/** Check if a product is wishlisted (fast, localStorage-based). */
export function isWishlisted(productId) {
  return readLocal().includes(Number(productId));
}

/** Get wishlist count (fast, localStorage-based). */
export function wishlistCount() {
  return readLocal().length;
}
